// A sound's Beats in the Parameters, after Fade (spec find-beats.md): Find beats hears its file in
// this browser (`engine/beat-read.ts`), finds its beats (`engine/beats.ts`) and marks them as one
// undo step; Find again replaces them, and Clear beats takes them all away. A find stops when the
// section closes, so its beats land only on the sound it was for.
import { Button, Progress, Text } from "@mantine/core";
import { Music } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { hearSound } from "./engine/beat-read";
import { findBeats } from "./engine/beats";
import { ParamSection } from "./params";
import { beatReach, beatTimes, type AudioClip, type TimelineEdit } from "./timeline";
import classes from "./editor.module.css";

/** Where a find is: none running, reading `part` of the sound (0 to 1), done with none, or failed. */
type Finding = { state: "idle" } | { state: "finding"; part: number } | { state: "none" } | { state: "failed" };

export function BeatsSection(props: {
  clip: AudioClip;
  /** The sound's file, null while it is not ready. */
  url: string | null;
  sourceMs: number | null;
  onEdit: (edit: TimelineEdit) => void;
}) {
  const { clip, url, sourceMs, onEdit } = props;
  const [finding, setFinding] = useState<Finding>({ state: "idle" });
  const running = useRef<AbortController | null>(null);
  useEffect(() => () => running.current?.abort(), []);
  const hear = async (file: string) => {
    running.current?.abort();
    const run = new AbortController();
    running.current = run;
    setFinding({ state: "finding", part: 0 });
    try {
      const heard = await hearSound(file, beatReach(clip, sourceMs), run.signal, (part) => {
        if (!run.signal.aborted) setFinding({ state: "finding", part });
      });
      if (run.signal.aborted) return;
      const found = heard === null ? [] : findBeats(heard.samples, heard.rate).map((ms) => ms + heard.fromMs);
      onEdit({ type: "setBeats", id: clip.id, beatsMs: found });
      setFinding({ state: found.length === 0 ? "none" : "idle" });
    } catch {
      if (!run.signal.aborted) setFinding({ state: "failed" });
    }
  };
  const find = () => {
    if (url !== null) void hear(url);
  };
  const shown = beatTimes(clip).length;
  if (finding.state === "finding") {
    return (
      <ParamSection title="Beats">
        <Text className={classes.paramNote}>Finding beats…</Text>
        <Progress size={4} value={finding.part * 100} aria-label="Finding beats" />
      </ParamSection>
    );
  }
  if (finding.state === "failed") {
    return (
      <ParamSection title="Beats">
        <Text className={classes.paramNote} role="alert">
          The beats could not be found.
        </Text>
        <Button className={classes.paramButton} size="xs" variant="default" disabled={url === null} onClick={find}>
          Try again
        </Button>
      </ParamSection>
    );
  }
  if (clip.beatsMs.length > 0) {
    return (
      <ParamSection title="Beats">
        <div className={classes.paramHeadRow}>
          <Text className={classes.paramLabel}>{shown === 1 ? "1 beat" : `${shown} beats`}</Text>
          <Button size="xs" variant="subtle" disabled={url === null} onClick={find}>
            Find again
          </Button>
        </div>
        <Button
          className={classes.paramButton}
          size="xs"
          variant="subtle"
          color="red"
          onClick={() => onEdit({ type: "setBeats", id: clip.id, beatsMs: [] })}
        >
          Clear beats
        </Button>
      </ParamSection>
    );
  }
  return (
    <ParamSection title="Beats">
      <Button
        className={classes.paramButton}
        size="xs"
        variant="default"
        leftSection={<Music size={13} aria-hidden />}
        disabled={url === null}
        onClick={find}
      >
        Find beats
      </Button>
      <Text className={classes.paramNote}>
        {finding.state === "none" ? "No beat was found in this sound." : "Found in this browser. Nothing is uploaded."}
      </Text>
    </ParamSection>
  );
}
